import React, { createContext, ReactNode, useEffect, useState, useCallback } from 'react';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { useGetThemeFilterOptionsQuery, Wws_ThemeFilterOption, Wws_ThemeFilterOptions } from '../../generated/graphql';
import { usePublicClient } from '../../apollo/ApolloClientsProvider';

export const titleCase = (str: string) =>
  str.replace(/\w\S*/g, function(txt) {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });

export type SetFilter = (colors: string[] | null, styles: string[] | null, seasons: string[] | null) => void;

export interface ThemeFilters {
  colors: string[] | null;
  styles: string[] | null;
  seasons: string[] | null;
}

export type ThemeFilterOptions = Omit<Wws_ThemeFilterOptions, 'styles'>;

const ThemeFiltersContext = createContext<{
  themeFilterOptions?: ThemeFilterOptions;
  themeFilterOptionsDisplayNamesMap?: Record<string, string>;
  loading: boolean;
  themeFilters: ThemeFilters;
  setFilters: SetFilter;
}>({
  themeFilters: { colors: null, styles: null, seasons: null },
  loading: true,
  setFilters: () => {},
});

interface Props {
  children: ReactNode | ReactNode[];
}

// parse query param for filter values
// ensure it exists in our list of filter options
const parseQueryParam = (param: string | string[] | null, filterOptionKeys: string[]): string[] | null => {
  // if an array of filter options is passed
  if (param?.constructor.name === 'Array') {
    const filtered = [...(param as string[])].reduce((arr: string[], el: string) => {
      const filterOption = el.toLocaleLowerCase() as string;
      if (filterOptionKeys.indexOf(filterOption) !== -1) {
        arr.push(filterOption);
      }
      return arr;
    }, []);
    return filtered;
  }

  // if a single filter option is passed as a string filter and convert it to an array
  if (typeof param === 'string' && filterOptionKeys.includes(param.toLocaleLowerCase())) {
    return [param.toLocaleLowerCase()];
  }

  return null;
};

const buildThemeFiltersDisplayNameMap = (
  styles: Wws_ThemeFilterOption[] | undefined,
  colors: string[] | undefined,
  seasons: string[] | undefined
): Record<string, string> => {
  const displayNameMap: Record<string, string> = {};

  colors?.forEach(colorOption => {
    displayNameMap[colorOption] = titleCase(colorOption);
  });

  styles?.forEach(styleOption => {
    displayNameMap[styleOption.key] = styleOption.label;
  });

  seasons?.forEach(seasonOption => {
    displayNameMap[seasonOption] = titleCase(seasonOption);
  });

  return displayNameMap;
};

export const ThemeFiltersVariantProvider: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const publicClient = usePublicClient();
  const { data: themeFilterOptionsData } = useGetThemeFilterOptionsQuery({ client: publicClient });
  const [themeFilters, setThemeFilters] = useState<ThemeFilters>({ colors: null, styles: null, seasons: null });
  const [loading, setLoading] = useState<boolean>(true);
  const [themeFilterOptions, setThemeFilterOptions] = useState<Omit<ThemeFilterOptions, 'styles'>>({
    colors: [],
    seasons: [],
    themeStyles: [],
  });
  const [themeFilterOptionsDisplayNamesMap, setThemeFilterOptionsDisplayNamesMap] = useState<Record<string, string>>(
    {}
  );

  const isThemeFilterOptionsEmpty = (options: ThemeFilterOptions) => {
    return options.colors.length === 0 && options.seasons.length === 0 && options.themeStyles.length === 0;
  };

  useEffect(() => {
    if (!themeFilterOptionsData) return;

    setThemeFilterOptions(themeFilterOptionsData.themeFilterOptions);

    setThemeFilterOptionsDisplayNamesMap(
      buildThemeFiltersDisplayNameMap(
        themeFilterOptionsData.themeFilterOptions.themeStyles,
        themeFilterOptionsData.themeFilterOptions.colors,
        themeFilterOptionsData.themeFilterOptions.seasons
      )
    );
  }, [themeFilterOptionsData]);

  const handleQueryParamFilter = useCallback(
    (qs: queryString.ParsedQuery<string>, themeFilterOptions: ThemeFilterOptions) => {
      if (isThemeFilterOptionsEmpty(themeFilterOptions)) return;

      setThemeFilters({
        colors: parseQueryParam(qs.colors, themeFilterOptions.colors),
        styles: parseQueryParam(
          qs.styles,
          themeFilterOptions.themeStyles.map(style => style.key)
        ),
        seasons: parseQueryParam(qs.seasons, themeFilterOptions.seasons),
      });
      if (loading) setLoading(false);
    },
    [setThemeFilters, setLoading, loading]
  );

  useEffect(() => {
    const qs = queryString.parse(location.search, { arrayFormat: 'comma' });
    handleQueryParamFilter(qs, themeFilterOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeFilterOptions]);

  const handleSetThemeFilters = useCallback(
    (colors: string[] | null, styles: string[] | null, seasons: string[] | null) => {
      if (isThemeFilterOptionsEmpty(themeFilterOptions)) return;
      // set the filter query params, leaving any other param (utm_*, gclid, ...) untouched
      // read from history.location so the callback stays stable across search changes
      const params = new URLSearchParams(history.location.search);
      const setFilterParam = (key: string, values: string[] | null) => {
        if (values?.length) {
          params.set(key, values.join(','));
        } else {
          params.delete(key);
        }
      };
      setFilterParam('colors', colors);
      setFilterParam('styles', styles);
      setFilterParam('seasons', seasons);

      history.push({ search: params.toString() });

      // update filters
      setThemeFilters({
        colors,
        styles,
        seasons,
      });
      if (loading) setLoading(false);
    },
    [setThemeFilters, setLoading, loading, history, themeFilterOptions]
  );

  return (
    <ThemeFiltersContext.Provider
      value={{
        themeFilterOptions,
        themeFilterOptionsDisplayNamesMap,
        themeFilters,
        loading,
        setFilters: handleSetThemeFilters,
      }}
    >
      {children}
    </ThemeFiltersContext.Provider>
  );
};

export default ThemeFiltersContext;
